import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ApiClientService, AuthenticationService, ToastService } from '@services/index';
import { Align, Permission } from 'app/enums';
import { Inbox, SortType } from 'app/types';
import { BehaviorSubject, EMPTY, finalize, map, Observable, of, switchMap } from 'rxjs';
import { GetInboxesResponse } from '@services/api-client/responses';
import { sortStrings } from '@helpers/sort-string-helper';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalHelper } from '@helpers/modal-helper';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Filter, FilterForm } from './inbox-list.model';
import { Router } from '@angular/router';
import { APP_ROUTES } from 'app/constants';
import { AgreementRoutes } from 'app/features/agreement';

@UntilDestroy()
@Component({
    selector: 'app-inbox-list',
    templateUrl: './inbox-list.component.html',
    styleUrl: './inbox-list.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class InboxListComponent implements OnInit {
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly apiClientService = inject(ApiClientService);
    private readonly translateService = inject(TranslateService);
    private readonly modalService = inject(NgbModal);
    private readonly toastService = inject(ToastService);
    private readonly router = inject(Router);
    private readonly authenticationService = inject(AuthenticationService);

    public readonly align = Align;
    public sortKey: keyof Inbox = 'fileName';
    public sortAscending = true;
    public loading = false;
    public filterForm: FormGroup<FilterForm>;
    private readonly defaultFilter: Filter = {
        searchPhrase: ''
    };
    public loadingIds: string[] = [];
    public permissions = Permission;
    public pdfByteArray: ArrayBuffer;

    private readonly searchSubject: BehaviorSubject<void> = new BehaviorSubject<void>(undefined);
    public search$: Observable<void> = this.searchSubject.asObservable();
    private readonly sortSubject: BehaviorSubject<SortType<Inbox>> = new BehaviorSubject<SortType<Inbox>>({
        key: this.sortKey,
        ascending: this.sortAscending
    });
    public sort$: Observable<SortType<Inbox>> = this.sortSubject.asObservable();
    public inbox$: Observable<GetInboxesResponse>;
    private readonly deletedIdsSubject: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
    public deletedIds$: Observable<string[]> = this.deletedIdsSubject.asObservable();

    public get f() {
        return this.filterForm.controls;
    }

    public ngOnInit() {
        const fb = new FormBuilder();
        this.filterForm = fb.nonNullable.group(this.defaultFilter);

        this.inbox$ = this.apiClientService.getInboxItems$().pipe(
            map((response: GetInboxesResponse) => {
                return response;
            }),
            switchMap((inbox: Inbox[]) =>
                this.deletedIds$.pipe(map((deletedIds) => inbox.filter((c) => !deletedIds.includes(c.id))))
            ),
            switchMap((inboxes: Inbox[]) =>
                this.search$.pipe(
                    map(() => {
                        return this.filterResponse(inboxes);
                    })
                )
            ),
            switchMap((inboxes: Inbox[]) =>
                this.sort$.pipe(
                    switchMap((sort) => {
                        this.sortKey = sort.key;
                        this.sortAscending = sort.ascending;
                        this.loading = false;
                        this.changeDetectorRef.markForCheck();

                        return of(sortStrings<Inbox>(inboxes, sort.key, sort.ascending));
                    })
                )
            )
        );

        this.f.searchPhrase.valueChanges.subscribe(() => this.searchSubject.next());
    }

    public get inboxEmail() {
        return this.authenticationService.getUser().inbox;
    }

    public onSortChange(sortKey: keyof Inbox, sortAscending = true) {
        this.sortSubject.next({ key: sortKey, ascending: sortAscending });
    }

    public goToCreateNewAgreement(inbox: Inbox) {
        void this.router.navigate([`/${APP_ROUTES.Agreement}/${AgreementRoutes.CreateNewAgreement}`], {
            queryParams: {
                fileurl: this.getFileUrl(inbox),
                id: inbox.id,
                filename: inbox.fileName
            }
        });
    }

    private getFileUrl(inbox: Inbox) {
        return `inbox/${inbox.createdBy}/${inbox.id}_${inbox.fileName}`;
    }

    public view(inbox: Inbox) {
        if (inbox.fileName.endsWith('.pdf')) {
            const url = this.getFileUrl(inbox);
            this.apiClientService.downloadTempFile(url).subscribe((response) => {
                this.pdfByteArray = new Uint8Array(response);
                this.changeDetectorRef.markForCheck();
            });
        }
    }

    private filterResponse(response: GetInboxesResponse) {
        const searchPhrase = this.f.searchPhrase.value.toLowerCase();

        return response.filter((counterpart) =>
            Object.values(counterpart).some(
                (value) => typeof value === 'string' && value.toLowerCase().includes(searchPhrase)
            )
        );
    }

    public createAgreement(event: Event, inbox: Inbox) {
        event.stopPropagation();
        if (this.loadingIds.includes(inbox.id)) {
            return;
        }
        const modalRef = this.modalService.open(ConfirmModalComponent);
        const modalInstanceHelper = ModalHelper.getConfirmModalInstanceHelper(modalRef);

        modalInstanceHelper.componentInstance.model = {
            titleLanguageKey: 'TITLES.INBOX_CREATE_AGREEMENT',
            bodyLanguageKey: 'TEXTS.CONFIRM_INBOX_CREATE_AGREEMENT',
            cancelLanguageKey: 'BUTTONS.CANCEL',
            confirmLanguageKey: 'BUTTONS.CONFIRM'
        };

        modalInstanceHelper.confirmation$
            .pipe(
                untilDestroyed(this),
                switchMap((confirm) => {
                    if (confirm) {
                        this.goToCreateNewAgreement(inbox);
                    }
                    return EMPTY;
                })
            )
            .subscribe({
                next: () => {
                    const title = this.translateService.instant('TOAST_TITLES.SUCCESS') as string;
                    const message = this.translateService.instant(
                        'TOAST_MESSAGES.INBOX_CREATE_AGREEMENT_SUCCESS'
                    ) as string;

                    this.toastService.open(title, message, { type: 'success', progressBar: true });
                    this.deletedIdsSubject.next([...this.deletedIdsSubject.value, inbox.id]);
                },
                error: () => {
                    const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                    const message = this.translateService.instant(
                        'TOAST_MESSAGES.INBOX_CREATE_AGREEMENT_ERROR'
                    ) as string;

                    this.toastService.open(title, message, { type: 'error', progressBar: true });
                }
            });
    }

    public confirmDelete(event: Event, inbox: Inbox) {
        event.stopPropagation();
        if (this.loadingIds.includes(inbox.id)) {
            return;
        }
        const modalRef = this.modalService.open(ConfirmModalComponent);
        const modalInstanceHelper = ModalHelper.getConfirmModalInstanceHelper(modalRef);

        modalInstanceHelper.componentInstance.model = {
            titleLanguageKey: 'TITLES.DELETE_INBOX',
            bodyLanguageKey: 'TEXTS.CONFIRM_DELETE_INBOX',
            cancelLanguageKey: 'BUTTONS.CANCEL',
            confirmLanguageKey: 'BUTTONS.CONFIRM'
        };

        modalInstanceHelper.confirmation$
            .pipe(
                untilDestroyed(this),
                switchMap((confirm) => {
                    if (confirm) {
                        this.loadingIds.push(inbox.id);
                        this.changeDetectorRef.markForCheck();
                        return this.apiClientService.deleteInboxItem$(inbox.id);
                    }
                    return EMPTY;
                }),
                finalize(() => {
                    this.loadingIds = this.loadingIds.filter((id) => id != inbox.id);
                    this.changeDetectorRef.markForCheck();
                })
            )
            .subscribe({
                next: () => {
                    const title = this.translateService.instant('TOAST_TITLES.SUCCESS') as string;
                    const message = this.translateService.instant('TOAST_MESSAGES.DELETE_INBOX_SUCCESS') as string;

                    this.toastService.open(title, message, { type: 'success', progressBar: true });
                    this.deletedIdsSubject.next([...this.deletedIdsSubject.value, inbox.id]);
                },
                error: () => {
                    const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                    const message = this.translateService.instant('TOAST_MESSAGES.DELETE_INBOX_ERROR') as string;

                    this.toastService.open(title, message, { type: 'error', progressBar: true });
                }
            });
    }
}
